import { useIntl } from 'react-intl';
import parse from 'html-react-parser';
import { createElement } from 'react';

const defaultTagReplacers = {
    strong: chunks => createElement('strong', null, chunks),
    b: chunks => createElement('b', null, chunks),
    i: chunks => createElement('i', null, chunks),
    em: chunks => createElement('em', null, chunks),
    u: chunks => createElement('u', null, chunks),
    br: createElement('br', null),
    nbsp: '\u00A0',
};

/**
 * A hook that provides a function to format internationalized messages with custom tag replacements.
 * It uses the `useIntl` hook from `react-intl` for message formatting and `html-react-parser`
 * to parse HTML strings into React elements. By default, it supports basic HTML tags such as
 * <strong>, <b>, <i>, <em>, and <u>, but additional custom tag replacers can be provided.
 * 
 * @returns {Function} A function `formatWithTags` that takes a message ID and an optional
 *                     object of custom tag replacements. It returns the formatted message
 *                     string with tags parsed as React elements.
 * 
 * @throws Will log a warning to the console if message formatting fails, returning the message ID.
 */

export const useIntlTagFormatter = (skipParsing = true) => {
    const intl = useIntl();

    const parseString = (message) => {
        return typeof message === 'string' && !skipParsing ? parse(message) : message;
    };

    const intlTagFormatter = (id, customReplacers = {}) => {
        try {
            const enrichedReplacements = {
                ...(skipParsing ? defaultTagReplacers : {}),
                ...customReplacers,
            };

            const formattedMessage = intl.formatMessage({ id }, enrichedReplacements);
            return parseString(formattedMessage);
        } catch (error) {
            console.warn(`[i18n] Error formatting message with ID "${id}":`, error);
            return id;
        }
    };

    return intlTagFormatter;
};
